import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  finalize,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
  distinctUntilChanged,
  debounceTime,
} from 'rxjs';
import {
  ExtractHttpErrorResponseMessage,
  FilterFieldsToQueryParamsBuilder,
  IHttpPagedResponse,
  updateApiFilter,
  IFilterAndPaginationFieldsBase,
  IIrembogovBasicLabelKeyPair,
  ToastService,
  RolesEnum,
  IHttpErrorResponseCodeAndMessage,
  ExtractHttpErrorResponseCodeAndMessage,
} from '@irembo-andela/irembogov3-common';
import { HttpErrorResponse } from '@angular/common/http';
import { EServiceChangeRequestType } from '../../../../core/models/service-change-request-type.enum';
import { IServiceChangeRequest } from '../../../../core/models/service-change-request.model';
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import { EServiceChangeRequestStatus } from '../../../../core/models/service-change-request-status.enum';
import { Router } from '@angular/router';
import { IIremboOrganization } from '../../../../core/models/irembo-organization.model';
import { FormControl } from '@angular/forms';

interface IGetListParamsFields extends IFilterAndPaginationFieldsBase {
  status: EServiceChangeRequestStatus[] | null;
  sort: string;
  sortDirection: 'ASC' | 'DESC';
  serviceName: string;
  institutionId: string;
}
@Component({
  selector: 'irembogov-service-change-requests-list-table-widget',
  templateUrl: './service-change-requests-list-table.component.html',
  styleUrls: ['./service-change-requests-list-table.component.scss'],
})
export class ServiceChangeRequestsListTableWidgetComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() changeRequestStatus?: EServiceChangeRequestStatus;
  @Input() viewAsRole!:
    | RolesEnum.ROLE_QA_REVIEWER
    | RolesEnum.ROLE_SERVICE_MANAGER;

  RolesEnum = RolesEnum;

  changeRequestList: IServiceChangeRequest[] = [];
  organizationMap: Record<string, IIremboOrganization> = {};

  serviceNameFilter = new FormControl(null);
  institutionFilter = new FormControl(null);
  institutionFilterSearchItem: any = null;

  collectionSize = 0;

  alertMessage = '';

  isLoadingChangeRequestList = true;
  isLoadingOrganizationList = true;

  EServiceChangeRequestStatus = EServiceChangeRequestStatus;

  organizationFilterList: Record<string, unknown>[] = [];
  isLoadingOrganizationFilterList = false;
  organizationFilterPage = 0;
  organizationFilterSize = 50;
  allItemsLoaded = false;

  selectedSortOptionIndex = 0;

  sortFieldOptions: IIrembogovBasicLabelKeyPair<{
    column: string;
    direction: 'ASC' | 'DESC';
  }>[] = [
    {
      label: 'Date Created, Descending',
      key: { column: 'dateCreated', direction: 'DESC' },
    },
    {
      label: 'Date Created, Ascending',
      key: { column: 'dateCreated', direction: 'ASC' },
    },
  ];

  defaultParamsFields: IGetListParamsFields = {
    page: 0,
    size: 10,
    status: null,
    sort: this.sortFieldOptions[0].key.column,
    sortDirection: this.sortFieldOptions[0].key.direction,
    serviceName: '',
    institutionId: '',
  };

  updateApiFilter = updateApiFilter;

  getQueryParamsSubscription$!: Subscription;
  _filterObject: BehaviorSubject<IGetListParamsFields> =
    new BehaviorSubject<IGetListParamsFields>(this.defaultParamsFields);

  changeRequestForConfig!: IServiceChangeRequest;

  configChangeRequestOpen = false;

  EServiceChangeRequestType = EServiceChangeRequestType;
  changeRequest!: IServiceChangeRequest;

  constructor(
    private serviceChangeRequestService: ServiceChangeRequestService,
    private toastService: ToastService,
    private router: Router
  ) {
    this.getOrganizationsFilterList();
  }

  ngOnInit(): void {
    this.getQueryParamsSubscription$ = this._filterObject
      .asObservable()
      .pipe(
        switchMap((paramsFields: IGetListParamsFields) =>
          this.getAssignedServiceChangeRequestsForType(paramsFields)
        )
      )
      .subscribe();

    this.getQueryParamsSubscription$.add(
      this.serviceNameFilter.valueChanges
        .pipe(distinctUntilChanged(), debounceTime(400))
        .subscribe((value: string | null) => {
          // Reset to the first page: a narrower result set can have fewer pages
          // than the current page index, which would return an empty page.
          if (value?.trim()) {
            this.updateApiFilter(this._filterObject, {
              serviceName: value,
              page: 0,
            });
            return;
          }
          this.updateApiFilter(this._filterObject, {
            serviceName: '',
            page: 0,
          });
        })
    );

    this.getQueryParamsSubscription$.add(
      this.institutionFilter.valueChanges
        .pipe(distinctUntilChanged(), debounceTime(400))
        .subscribe((organization: Record<string, unknown> | null) => {
          if (organization?.['id']) {
            this.updateApiFilter(this._filterObject, {
              institutionId: <string>organization['id'],
              page: 0,
            });
            return;
          }
          this.getOrganizationsFilterList('', true);
          this.updateApiFilter(this._filterObject, {
            institutionId: '',
            page: 0,
          });
        })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['changeRequestStatus']) {
      this.reInitTableList();
    }
  }

  generateEmptyChangeRequestExtraMessage(): string {
    const messages: string[] = [];
    if (
      this.serviceNameFilter?.value &&
      (<string>this.serviceNameFilter?.value)?.trim()
    ) {
      messages.push(
        `with service name that contains '${this.serviceNameFilter?.value}'`
      );
    }
    if (this.institutionFilter?.value?.['name']) {
      messages.push(`in organization ${this.institutionFilter.value['name']}`);
    }

    return messages.length > 0 ? ' ' + messages.join(' and ') : '';
  }

  updateTableList(keyPair: Partial<IGetListParamsFields>) {
    if (keyPair.page) {
      keyPair.page--;
    }
    this._filterObject.next({
      ...this._filterObject.getValue(),
      ...keyPair,
    });
  }

  reInitTableList() {
    this.updateTableList({
      status: this.changeRequestStatus ? [this.changeRequestStatus] : null,
      page: this.defaultParamsFields.page ?? 0,
      size: this.defaultParamsFields.size ?? 10,
    });
  }

  getAssignedServiceChangeRequestsForType(
    requestParams: IGetListParamsFields
  ): Observable<IHttpPagedResponse<IServiceChangeRequest> | null> {
    this.isLoadingChangeRequestList = true;

    if (
      requestParams.status &&
      requestParams.status.indexOf(EServiceChangeRequestStatus.IN_DEVELOPMENT) >
        -1 &&
      requestParams.status.indexOf(
        EServiceChangeRequestStatus.IN_REDEVELOPMENT
      ) < 0
    ) {
      requestParams.status.push(EServiceChangeRequestStatus.IN_REDEVELOPMENT);
    }

    const queryParams: string =
      FilterFieldsToQueryParamsBuilder<IGetListParamsFields>(requestParams);

    return this.serviceChangeRequestService
      .getAssignedServiceChangeRequests(queryParams, this.viewAsRole)
      .pipe(
        tap((response: IHttpPagedResponse<IServiceChangeRequest>) => {
          this.changeRequestList = response.data.content;
          this.collectionSize = response.data.totalPages * response.data.size;
          this.mapOrganizationToChangeRequest(this.changeRequestList);
        }),
        catchError((err: HttpErrorResponse) => {
          const errorMessage = ExtractHttpErrorResponseMessage(
            err,
            'Failed to fetch change requests.'
          );
          this.toastService.show({
            body: `ERR: ${errorMessage}`,
            type: 'error',
          });
          this.changeRequestList = [];
          this.collectionSize = 0;
          this.isLoadingOrganizationList = false;
          return of(null);
        }),
        finalize(() => (this.isLoadingChangeRequestList = false))
      );
  }

  mapOrganizationToChangeRequest(changeRequestList: IServiceChangeRequest[]) {
    if (!changeRequestList || changeRequestList?.length < 1) {
      this.isLoadingOrganizationList = false;
      return;
    }
    this.isLoadingOrganizationList = true;
    const organizationIds = Array.from(
      new Set(
        changeRequestList.map(changeRequest => changeRequest.organizationId)
      )
    );
    const organizationListQueryStr: string =
      'id=' + organizationIds.join('&id=');
    const queryParams = `page=${0}&size=${
      organizationIds.length
    }&${organizationListQueryStr}`;

    this.serviceChangeRequestService
      .getAssignedOrganizations(this.viewAsRole, queryParams)
      .pipe(finalize(() => (this.isLoadingOrganizationList = false)))
      .subscribe({
        next: (response: IHttpPagedResponse<IIremboOrganization>) => {
          response.data.content.forEach(organization => {
            this.organizationMap[organization.id] = organization;
          });

          this.changeRequestList = this.changeRequestList.map(changeRequest => {
            return {
              ...changeRequest,
              organization: this.organizationMap[changeRequest.organizationId],
            };
          });
        },
      });
  }

  getOrganizationsFilterList(name: string | null = '', resetList = false) {
    if (resetList) {
      this.organizationFilterList = [];
      this.organizationFilterPage = 0;
    }
    // ng-select emits a null search term when it clears its buffer on select,
    // which would otherwise be interpolated as the literal string "null".
    const searchName = (name ?? '').trim();
    this.isLoadingOrganizationFilterList = true;
    this.serviceChangeRequestService
      .getAssignedOrganizations(
        this.viewAsRole,
        `page=${this.organizationFilterPage}&size=${this.organizationFilterSize}&name=${searchName}`
      )
      .pipe(finalize(() => (this.isLoadingOrganizationFilterList = false)))
      .subscribe((response: IHttpPagedResponse<IIremboOrganization>) => {
        this.organizationFilterList = [
          ...this.organizationFilterList,
          ...(response.data.content as unknown as Record<string, unknown>[]),
        ];
        if (response.data.totalPages <= this.organizationFilterPage + 1) {
          this.allItemsLoaded = true;
        }
      });
  }

  loadMoreOrganizationsFilterList() {
    if (this.allItemsLoaded) {
      return;
    }
    this.organizationFilterPage++;
    this.getOrganizationsFilterList();
  }

  updateSort(optionIndex: number): void {
    const selectedSortOption = this.sortFieldOptions[optionIndex];
    if (!selectedSortOption) {
      return;
    }
    this.updateApiFilter(this._filterObject, {
      sort: selectedSortOption.key.column,
      sortDirection: selectedSortOption.key.direction,
      page: 0,
    });
  }

  setChangeRequestForConfig(changeRequest: IServiceChangeRequest) {
    this.changeRequestForConfig = changeRequest;
    this.updatedConfigChangeRequestViewState(true);
  }

  updatedConfigChangeRequestViewState($event: boolean) {
    this.configChangeRequestOpen = $event;
  }

  async onConfigureRequestClick(requestId: string) {
    try {
      await this.router.navigate(['/services/configure', requestId]);
    } catch (e: unknown) {
      return;
    }
  }

  async onReviewRequestClick(requestId: string) {
    try {
      await this.router.navigate(['/services/review', requestId]);
    } catch (e: unknown) {
      return;
    }
  }

  async onViewRequestClick(requestId: string) {
    try {
      await this.router.navigate(['/services/view', requestId]);
    } catch (e: unknown) {
      return;
    }
  }

  private handleDebugModeSuccess(
    response: any,
    action: 'enabled' | 'disabled'
  ) {
    this.changeRequest = response.data;

    this.toastService.show({
      body: `Change request debug mode ${action}`,
      type: 'success',
    });
    this._filterObject.next(this._filterObject.getValue());
  }

  private handleDebugModeError(error: any) {
    const errorMessage = ExtractHttpErrorResponseMessage(
      error,
      error.error.responseMessage
    );
    this.toastService.show({ body: errorMessage, type: 'error' });
  }

  enableDebugMode(requestId: string) {
    if (!requestId) return;

    this.serviceChangeRequestService
      .enableDebugMode(requestId)
      .pipe()
      .subscribe({
        next: response => this.handleDebugModeSuccess(response, 'enabled'),
        error: error => this.handleDebugModeError(error),
      });
  }

  disableDebugMode(requestId: string) {
    if (!requestId) return;

    this.serviceChangeRequestService
      .disableDebugMode(requestId)
      .pipe()
      .subscribe({
        next: response => this.handleDebugModeSuccess(response, 'disabled'),
        error: error => this.handleDebugModeError(error),
      });
  }

  onToggleDebugMode(isChecked: boolean, requestId: string | undefined) {
    if (!requestId) {
      return;
    }
    if (isChecked) {
      this.enableDebugMode(requestId);
    } else {
      this.disableDebugMode(requestId);
    }
  }

  ngOnDestroy(): void {
    if (this.getQueryParamsSubscription$) {
      this.getQueryParamsSubscription$.unsubscribe();
    }
  }
}
